import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import { useAuthUser } from '../../hooks/useAuthUser';

const InfomediaPage = () => {
  const { user } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [mediaData] = useState({
    totalCampaigns: 42,
    activeAccounts: 8,
    monthlySpend: 125430.50,
    avgCTR: 2.34,
    avgCPC: 0.65,
    totalImpressions: 2847362
  });

  useEffect(() => {
    // Simular carga de datos
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('es-AR').format(number);
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col xs="auto">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="align-items-center justify-content-between g-3 mb-4">
        <Col xs="auto">
          <h2 className="text-primary fw-bold mb-0">
            <FontAwesomeIcon icon="info-circle" className="me-2" />
            Infomedia
          </h2>
          <p className="text-muted mb-0">Panel informativo para clientes</p>
        </Col>
        <Col xs="auto">
          <Badge bg="info" className="fs-6">
            <FontAwesomeIcon icon="user" className="me-1" />
            Cliente: {user?.fullName || 'Usuario'}
          </Badge>
        </Col>
      </Row>

      {/* Bienvenida personalizada */}
      <Row className="mb-4">
        <Col>
          <Alert variant="info" className="border-0 shadow-sm">
            <FontAwesomeIcon icon="star" className="me-2" />
            <strong>¡Bienvenido, {user?.fullName || 'Usuario'}!</strong>
            <br />
            Este es tu panel informativo donde puedes consultar el rendimiento de tus campañas publicitarias.
          </Alert>
        </Col>
      </Row>

      {/* Métricas principales */}
      <Row className="g-3 mb-4">
        <Col lg={4} md={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-1 text-primary mb-2">
                <FontAwesomeIcon icon="bullhorn" />
              </div>
              <h3 className="text-dark fw-bold mb-1">{mediaData.totalCampaigns}</h3>
              <p className="text-muted mb-0">Campañas Activas</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4} md={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-1 text-success mb-2">
                <FontAwesomeIcon icon="credit-card" />
              </div>
              <h3 className="text-dark fw-bold mb-1">{mediaData.activeAccounts}</h3>
              <p className="text-muted mb-0">Cuentas Publicitarias</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4} md={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="fs-1 text-warning mb-2">
                <FontAwesomeIcon icon="dollar-sign" />
              </div>
              <h3 className="text-dark fw-bold mb-1">{formatCurrency(mediaData.monthlySpend)}</h3>
              <p className="text-muted mb-0">Inversión del Mes</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Métricas de rendimiento */}
      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <FalconCardHeader title="Métricas de Rendimiento" titleTag="h5" />
            <Card.Body>
              <Row className="g-3">
                <Col sm={6}>
                  <div className="d-flex align-items-center">
                    <div className="fs-4 text-info me-3">
                      <FontAwesomeIcon icon="mouse-pointer" />
                    </div>
                    <div>
                      <h6 className="mb-0 text-dark fw-semibold">{mediaData.avgCTR}%</h6>
                      <small className="text-muted">CTR Promedio</small>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-center">
                    <div className="fs-4 text-success me-3">
                      <FontAwesomeIcon icon="coins" />
                    </div>
                    <div>
                      <h6 className="mb-0 text-dark fw-semibold">${mediaData.avgCPC}</h6>
                      <small className="text-muted">CPC Promedio</small>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <FalconCardHeader title="Alcance Total" titleTag="h5" />
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="fs-1 text-primary me-3">
                  <FontAwesomeIcon icon="eye" />
                </div>
                <div>
                  <h4 className="mb-0 text-dark fw-bold">{formatNumber(mediaData.totalImpressions)}</h4>
                  <p className="text-muted mb-0">Impresiones Totales</p>
                  <small className="text-success">
                    <FontAwesomeIcon icon="arrow-up" className="me-1" />
                    +15.3% vs mes anterior
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Información adicional */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <FalconCardHeader title="Información de Contacto" titleTag="h5" />
            <Card.Body>
              <Alert variant="light" className="border border-primary">
                <FontAwesomeIcon icon="info-circle" className="text-primary me-2" />
                <strong>¿Necesitas más información o tienes dudas sobre tus campañas?</strong>
                <br />
                Contacta a tu ejecutivo de cuenta o al equipo de soporte:
                <br />
                <FontAwesomeIcon icon="envelope" className="me-1 mt-2" />
                <strong>Email:</strong> soporte@herrera.com
                <br />
                <FontAwesomeIcon icon="phone" className="me-1" />
                <strong>Teléfono:</strong> +54 11 4000-0000
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InfomediaPage; 